/**
 * The password rules, defined ONCE for the whole web app.
 *
 * These lived as literals inside the Settings → Security panel. The moment a second screen needs
 * to ask for a password (the forced first-sign-in change), a copy would drift from the original,
 * and the two screens would disagree about what is acceptable — the class of defect where a user
 * is told 8 characters is fine, types 9, and is rejected by the server with no explanation.
 *
 * MIN_PASSWORD_LENGTH MIRRORS `apps/api/src/lib/password-policy.ts`. The API cannot import from
 * @jurivoxa/shared (its tsconfig `rootDir` is `src`), so the value is duplicated deliberately and
 * pinned by a test on each side. Change one, change the other.
 */
export const MIN_PASSWORD_LENGTH = 8;

export interface PasswordFormValue {
  /** Empty string when the caller does not require the current password (e.g. reset-by-link). */
  current: string;
  next: string;
  confirm: string;
}

export const emptyPasswordForm: PasswordFormValue = { current: '', next: '', confirm: '' };

/**
 * 0–4. Length carries two of the four points because length is what actually resists guessing.
 * The second length point is a fixed 12, deliberately NOT tied to MIN_PASSWORD_LENGTH: the minimum
 * is what the server will ACCEPT, the meter is what it ENCOURAGES. Tying them together would make
 * both points fire at the minimum and stop rewarding a longer password at all.
 */
const LONGER_PASSWORD_TIER = 12;

export function passwordStrength(pw: string): number {
  let score = 0;
  if (pw.length >= MIN_PASSWORD_LENGTH) score++;
  if (pw.length >= LONGER_PASSWORD_TIER) score++;
  if (/[A-Z]/.test(pw) && /[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score;
}

export interface PasswordRequirement {
  label: string;
  met: boolean;
}

/**
 * Only the LENGTH rule is enforced by the server. The other three are shown as guidance and feed
 * the strength meter — stating them as hard requirements when the API does not enforce them would
 * be the same lie in the opposite direction.
 */
export function passwordRequirements(pw: string): PasswordRequirement[] {
  return [
    { label: `At least ${MIN_PASSWORD_LENGTH} characters`, met: pw.length >= MIN_PASSWORD_LENGTH },
    { label: 'An uppercase letter', met: /[A-Z]/.test(pw) },
    { label: 'A number', met: /[0-9]/.test(pw) },
    { label: 'A symbol', met: /[^A-Za-z0-9]/.test(pw) },
  ];
}

export function passwordsMatch(form: PasswordFormValue): boolean {
  return !form.confirm || form.next === form.confirm;
}

/**
 * Ready to submit. `requireCurrent` is false only where the caller has already proven identity by
 * another means (a reset link). The length check mirrors the server so the button does not enable
 * for something the API will reject.
 */
export function isPasswordFormReady(form: PasswordFormValue, requireCurrent = true): boolean {
  if (requireCurrent && !form.current) return false;
  if (form.next.length < MIN_PASSWORD_LENGTH) return false;
  if (!form.confirm || form.next !== form.confirm) return false;
  // A forced change exists to replace a password someone else knows. Re-entering it would satisfy
  // the form while leaving the shared secret in place — the server rejects this too.
  if (requireCurrent && form.current === form.next) return false;
  return true;
}
